import apiClient from '../apiClient';
import { 
  PlansResponse, 
  SubscriptionsResponse, 
  ActiveSubscriptionResponse,
  PaymentsResponse,
  CreateOrderResponse,
  VerifyPaymentResponse,
  InvoiceResponse,
  GSTDetails
} from '../../types/subscription.types';

// Base API path
const BASE_PATH = '/api';

/**
 * Get all subscription plans
 */
export const getPlans = async (): Promise<PlansResponse> => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/plans`);
    return response.data;
  } catch (error) {
    console.error('Error fetching plans:', error);
    throw error;
  }
};

/**
 * Get user's subscriptions
 */
export const getUserSubscriptions = async (): Promise<SubscriptionsResponse> => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/subscriptions`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user subscriptions:', error);
    throw error;
  }
};

/**
 * Get user's active subscription
 */
export const getActiveSubscription = async (): Promise<ActiveSubscriptionResponse> => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/subscriptions/active`);
    return response.data;
  } catch (error) {
    console.error('Error fetching active subscription:', error);
    throw error;
  }
};

/**
 * Set active subscription
 */
export const setActiveSubscription = async (subscriptionId: string): Promise<ActiveSubscriptionResponse> => {
  try {
    const response = await apiClient.put(`${BASE_PATH}/subscriptions/${subscriptionId}/activate`);
    return response.data;
  } catch (error) {
    console.error('Error setting active subscription:', error);
    throw error;
  }
};

/**
 * Get user's payment history
 */
export const getPaymentHistory = async (limit: number = 10): Promise<PaymentsResponse> => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/payments`, {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching payment history:', error);
    throw error;
  }
};

/**
 * Create subscription order
 * @param planCode - The code of the plan to purchase
 * @param gstDetails - Optional GST details for the invoice
 * @param useDirectPaymentLink - Whether to use a direct payment link instead of Razorpay SDK
 */
export const createOrder = async (
  planCode: string, 
  gstDetails?: GSTDetails,
  useDirectPaymentLink: boolean = true
): Promise<CreateOrderResponse> => {
  try {
    console.log('Creating order for plan:', planCode, 
      'with GST details:', gstDetails || 'none', 
      'using direct payment link:', useDirectPaymentLink);
    
    const response = await apiClient.post(`${BASE_PATH}/payments/create-order`, {
      planCode,
      gstDetails,
      useDirectPaymentLink
    });
    return response.data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

/**
 * Verify payment and activate subscription
 */
export const verifyPayment = async (
  razorpayOrderId: string,
  razorpayPaymentId: string,
  razorpaySignature: string,
  gstDetails?: GSTDetails
): Promise<VerifyPaymentResponse> => {
  try {
    console.log('Verifying payment:', {
      orderId: razorpayOrderId,
      paymentId: razorpayPaymentId,
      // Don't log signature for security
      hasGstDetails: !!gstDetails
    });
    
    const response = await apiClient.post(`${BASE_PATH}/payments/verify`, {
      razorpayOrderId,
      razorpayPaymentId,
      razorpaySignature,
      gstDetails
    });
    return response.data;
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw error;
  }
};

/**
 * Get invoice for a payment
 */
export const getInvoice = async (paymentId: string): Promise<InvoiceResponse> => {
  try {
    const response = await apiClient.get(`${BASE_PATH}/payments/${paymentId}/invoice`);
    return response.data;
  } catch (error) {
    console.error('Error getting invoice:', error);
    throw error;
  }
};

/**
 * Save GST details for user
 */
export const saveGSTDetails = async (gstDetails: GSTDetails): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.post(`${BASE_PATH}/payments/gst-details`, gstDetails);
    return response.data;
  } catch (error) {
    console.error('Error saving GST details:', error);
    throw error;
  }
};

export default {
  getPlans,
  getUserSubscriptions,
  getActiveSubscription,
  setActiveSubscription,
  getPaymentHistory,
  createOrder,
  verifyPayment,
  getInvoice,
  saveGSTDetails
};
